import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorBoundary from '@/components/ErrorBoundary'
import SessionManager from '@/components/SessionManager'
import { useAuthStore } from '@/stores/auth.store'
import {
  Squares2X2Icon, UserGroupIcon, IdentificationIcon, ShieldCheckIcon,
  DocumentTextIcon, PuzzlePieceIcon, EnvelopeIcon, ChartBarIcon,
  BuildingOffice2Icon, PresentationChartLineIcon, ArrowLeftOnRectangleIcon,
  Bars3Icon, XMarkIcon,
} from '@heroicons/react/24/outline'

/**
 * Guscio dell'AMMINISTRAZIONE DI SISTEMA (Shell B — UX-01).
 *
 * Separa l'esperienza amministrativa da quella di prodotto: le rotte
 * /app/admin/* e /app/crm-marketing/* vivono QUI, fuori dall'AppLayout utente
 * (niente nav di prodotto, FAB, onboarding o banner profilo). Chrome dedicato +
 * link esplicito per tornare all'app. Vi si accede solo con i guard
 * requireAdmin / requireSales già presenti sulle rotte.
 */

interface NavEntry { to: string; label: string; icon: React.ElementType; match?: string[] }

const CONSOLE_NAV: NavEntry[] = [
  { to: '/app/admin/hub',   label: 'Panoramica',       icon: Squares2X2Icon,            match: ['/app/admin/hub', '/app/admin/stats'] },
  { to: '/app/admin/users', label: 'Utenti & RBAC',    icon: UserGroupIcon,             match: ['/app/admin/users', '/app/admin/role-requests', '/app/admin/rbac-policy', '/app/admin/doctor-verification', '/app/admin/certifications'] },
  { to: '/app/admin/party', label: 'Anagrafica',       icon: IdentificationIcon },
  { to: '/app/admin/gdpr-sagas', label: 'GDPR',        icon: ShieldCheckIcon,           match: ['/app/admin/gdpr'] },
  { to: '/app/admin/content-pages', label: 'Contenuti', icon: DocumentTextIcon,         match: ['/app/admin/content'] },
  { to: '/app/admin/integrations', label: 'Integrazioni', icon: PuzzlePieceIcon,        match: ['/app/admin/integrations', '/app/admin/services', '/app/admin/config'] },
  { to: '/app/admin/comms', label: 'Comunicazioni',    icon: EnvelopeIcon,              match: ['/app/admin/comms', '/app/admin/whatsapp'] },
  { to: '/app/admin/directory/diving-centers', label: 'Catalogo centri', icon: BuildingOffice2Icon },
  { to: '/app/crm-marketing', label: 'CRM & Marketing', icon: PresentationChartLineIcon, match: ['/app/crm-marketing'] },
]

function ConsoleNav({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation()
  return (
    <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5" aria-label="Console di sistema">
      {CONSOLE_NAV.map(({ to, label, icon: Icon, match }) => {
        const active = pathname === to || pathname.startsWith(to + '/') || !!match?.some(p => pathname.startsWith(p))
        return (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              active ? 'bg-white/15 text-white font-medium' : 'text-white/75 hover:bg-white/10 hover:text-white'
            }`}
          >
            <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            {label}
          </NavLink>
        )
      })}
    </nav>
  )
}

export default function ConsoleShell() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const adminEmail = useAuthStore(s => s.user?.email)

  // Stessa area autenticata dell'AppLayout: il tema scuro per le classi grezze (styles/dark-compat.css)
  // vale solo con questo attributo. Senza, l'amministrazione restava bianca con il testo gia' invertito.
  useEffect(() => {
    document.documentElement.setAttribute('data-surface', 'app')
    return () => document.documentElement.removeAttribute('data-surface')
  }, [])

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Console sidebar (slate scuro per distinguerla dal prodotto) */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-60 flex-shrink-0 flex flex-col bg-slate-900 text-white
          transition-transform duration-200 lg:relative lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <span className="font-black tracking-tight" style={{ fontFamily: 'Montserrat, Inter, sans-serif' }}>eDive</span>
            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-600 text-white">Console</span>
          </div>
          <button onClick={() => setSidebarOpen(false)} aria-label="Chiudi menu" className="lg:hidden p-1 text-white/70 hover:text-white">
            <XMarkIcon className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <ConsoleNav onNavigate={() => setSidebarOpen(false)} />

        <div className="px-2 pb-3 border-t border-white/10 pt-2">
          <NavLink
            to="/app/dashboard"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
          >
            <ArrowLeftOnRectangleIcon className="w-5 h-5 flex-shrink-0 rotate-180" aria-hidden="true" />
            {t('nav.backToApp', "Torna all'app")}
          </NavLink>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar della console */}
        <header className="flex items-center gap-3 px-4 sm:px-6 h-14 border-b border-gray-200 bg-white">
          <button onClick={() => setSidebarOpen(true)} aria-label="Apri menu" className="lg:hidden p-1.5 -ml-1.5 text-gray-600 hover:text-gray-900">
            <Bars3Icon className="w-6 h-6" aria-hidden="true" />
          </button>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <ChartBarIcon className="w-4 h-4" aria-hidden="true" />
            <span className="font-semibold text-gray-700">{t('console.title', 'Console di sistema')}</span>
          </div>
          {adminEmail && <span className="ml-auto text-xs text-gray-400 truncate max-w-[40%]">{adminEmail}</span>}
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <ErrorBoundary key={pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <SessionManager />
    </div>
  )
}
